import * as React from 'react';
import { openWindow } from '../../services/openWindow';
import { OfferValue } from './Vehicles';
import { formatNumber } from '@progress/kendo-intl';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Button, SplitButton } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { bankersRounding } from 'utils/bankersRounding';
import { checkIcon, clockIcon, starIcon } from '@progress/kendo-svg-icons';
import AlternativeOfferDetails from './AlternativeOfferDetails';
import { dateFormatter } from 'utils/utils';

type Props = {
    quoteID: number;
    offer: OfferValue;

    email: (offer: OfferValue) => void;
    reply: (offer: OfferValue) => void;
    copyTemplate: (offer: OfferValue) => void;
    award: (offer: OfferValue) => void;
    reOffer: () => void;
    offerSelected: (total: number, offerId: number) => void;
};

type State = {
    visible: boolean;
}

export default class CarrierOfferButton extends React.Component<Props, State> {

    constructor(props: Props) {
        super(props);

        this.state = {
            visible: false,
        }

        this.toggleDialog = this.toggleDialog.bind(this);
        this.openCarrierProfile = this.openCarrierProfile.bind(this);
    }

    public render() {
        if (this.props.offer.Status === 0) {
            return <Button size="small" themeColor='info' onClick={(e) => {
                e.preventDefault();
                this.openCarrierProfile();
            }}>
                Waiting
            </Button>
        }

        if (this.props.offer.Status == 1 || this.props.offer.Status == 3 || this.props.offer.Status == 6) {
            return <SplitButton
                size='small'
                themeColor={this.props.offer.Status == 3 ? 'warning' : 'error'}
                title={this.props.offer.Note}
                onButtonClick={(e) => this.openCarrierProfile()}
                onItemClick={this.props.reOffer}
                text={this.props.offer.Status == 1 ? 'Declined' : this.props.offer.Status == 3 ? 'Expired' : 'Retracted'}
                items={[{ text: 'Re-Offer' }]}
            />
        }

        if (this.props.offer.Status == 2) {
            const statusIcon = this.props.offer.Awarded
                ? <SvgIcon icon={starIcon} className='k-button-icon' />
                : this.props.offer.Confirmed
                    ? <SvgIcon icon={checkIcon} className='k-button-icon' />
                    : undefined;

            const alternativeTimeIcon = (this.props.offer.AlternativePickup || this.props.offer.AlternativeDelivery) ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {this.props.offer.AlternativePickup && (
                        <span title={`Alt Pickup: ${dateFormatter(this.props.offer.AlternativePickup)}`}>
                            <SvgIcon icon={clockIcon} color="#0DCAf0" className='k-button-icon' />
                        </span>
                    )}
                    {this.props.offer.AlternativeDelivery && (
                        <span title={`Alt Delivery: ${dateFormatter(this.props.offer.AlternativeDelivery)}`}>
                            <SvgIcon icon={clockIcon} color="#FFC107" className='k-button-icon' />
                        </span>
                    )}
                </span>
            ) : undefined;
            const targetMargins = [0.12, 0.15, 0.20];
            const getRateForMargin = (margin: number) => bankersRounding(this.props.offer.AllInRate / (1 - margin));

            return <React.Fragment>
                <SplitButton
                    size="small"
                    themeColor='success'
                    buttonClass='pe-auto'
                    onButtonClick={this.toggleDialog}
                    onItemClick={(e) => {
                        const targetMargin = targetMargins[e.itemIndex];
                        if (targetMargin) {
                            this.props.offerSelected(getRateForMargin(targetMargin), this.props.offer.OfferID);
                        }
                    }}
                    title={this.props.offer.Note}
                    items={targetMargins.map(margin => `${formatNumber(getRateForMargin(margin), "c")} (${margin * 100}%)`)}
                    startIcon={statusIcon}
                    endIcon={alternativeTimeIcon}
                    text={formatNumber(this.props.offer.AllInRate, "c")}
                />
                {this.state.visible && <Dialog
                    title={this.props.offer.Confirmed ? "Book It Now" : "Bid Response"}
                    onClose={this.toggleDialog}
                    className='dialog-w800'
                >
                    <h3 className="text-center">{formatNumber(this.props.offer.AllInRate, "c")}</h3>
                    <p className="text-center">
                        by <Button fillMode='link' themeColor='primary' className='p-0' onClick={() => this.props.email(this.props.offer)}>{this.props.offer.ContactName}</Button> at <Button fillMode='link' themeColor='primary' className='p-0' onClick={this.openCarrierProfile}>{this.props.offer.AssetName}</Button></p>
                    {this.props.offer.Note && <p>Notes: {this.props.offer.Note}</p>}
                    {this.props.offer.IsAlternativeOffer && <AlternativeOfferDetails
                        originalAllInRate={this.props.offer.OriginalAllInRate}
                        alternativeAllInRate={this.props.offer.AlternativeAllInRate}
                        originalPickupScheduled={this.props.offer.OriginalPickupScheduled}
                        originalPickupLatest={this.props.offer.OriginalPickupLatest}
                        alternativePickup={this.props.offer.AlternativePickup}
                        originalDeliveryScheduled={this.props.offer.OriginalDeliveryScheduled}
                        originalDeliveryLatest={this.props.offer.OriginalDeliveryLatest}
                        alternativeDelivery={this.props.offer.AlternativeDelivery}
                    />}
                    <DialogActionsBar>
                        <SplitButton
                            text="Reply"
                            themeColor="primary"
                            buttonClass='flex-grow-1'
                            onButtonClick={() => this.props.reply(this.props.offer)}
                            onItemClick={() => this.props.copyTemplate(this.props.offer)}
                            items={[ 'Copy Email' ]}
                        />
                        {this.props.offer.Links.find(x => x.Name == 'Award') && <Button icon="star" svgIcon={starIcon} themeColor="success" onClick={() => this.props.award(this.props.offer)}>Award</Button>}
                        {this.props.offer.Awarded && <Button icon="star" svgIcon={starIcon} themeColor="success" disabled>Awarded</Button>}
                        {this.props.offer.FrontMessageID && <Button onClick={() => window.open(`https://app.frontapp.com/open/${this.props.offer.FrontMessageID}`, '_blank')}>View Email</Button>}
                        <Button themeColor='info' onClick={this.props.reOffer}>Re-Offer</Button>
                    </DialogActionsBar>
                </Dialog>}
            </React.Fragment>
        }
    }

    private toggleDialog() {
        this.setState({
            visible: !this.state.visible
        });
    }

    private openCarrierProfile() {
        if (this.props.offer.ID > 0) openWindow(`/Assets/Carrier/${this.props.offer.ID}`);
    }
}
